// "Leak check": finds long runs of a ROM's bytes inside a build file (plan.md §10). Runs locally only.
// Every BLOCK-aligned block of the ROM (skipping low-entropy filler) is hashed; a rolling hash over the file
// finds candidate windows at any offset, which are then compared byte for byte.

export const LEAK_BLOCK = 4096;

export interface LeakResult { matchedBlocks: number; matches: { fileOffset: number; romOffset: number }[] }

const B = 0x01000193; // FNV prime as rolling base (mod 2^32)

function powB(n: number): number {
  let r = 1;
  for (let i = 0; i < n; i++) r = Math.imul(r, B);
  return r >>> 0;
}

function interesting(rom: Uint8Array, off: number, len: number): boolean {
  const seen = new Uint8Array(256);
  let distinct = 0;
  for (let i = off; i < off + len; i++) if (!seen[rom[i]]++) if (++distinct >= 24) return true;
  return false;
}

function hashWindow(b: Uint8Array, off: number, len: number): number {
  let h = 0;
  for (let i = off; i < off + len; i++) h = (Math.imul(h, B) + b[i]) >>> 0;
  return h;
}

export function findRomRuns(rom: Uint8Array, file: Uint8Array, block = LEAK_BLOCK, maxReport = 16): LeakResult {
  const result: LeakResult = { matchedBlocks: 0, matches: [] };
  if (rom.length < block || file.length < block) return result;
  const table = new Map<number, number[]>();
  for (let off = 0; off + block <= rom.length; off += block) {
    if (!interesting(rom, off, block)) continue;
    const h = hashWindow(rom, off, block);
    const list = table.get(h);
    if (list) list.push(off); else table.set(h, [off]);
  }
  if (!table.size) return result;
  const out = powB(block);
  let h = hashWindow(file, 0, block);
  for (let i = 0; ; i++) {
    const cands = table.get(h);
    if (cands) {
      for (const romOff of cands) {
        let same = true;
        for (let k = 0; k < block; k++) if (file[i + k] !== rom[romOff + k]) { same = false; break; }
        if (same) {
          result.matchedBlocks++;
          if (result.matches.length < maxReport) result.matches.push({ fileOffset: i, romOffset: romOff });
          break;
        }
      }
    }
    if (i + block >= file.length) break;
    h = (Math.imul(h, B) + file[i + block] - Math.imul(file[i], out)) >>> 0;
  }
  return result;
}

/** Cheap header checks that need no ROM: does this file start like a cartridge image? */
export function looksLikeRom(bytes: Uint8Array): string | null {
  if (bytes.length >= 0x1000) {
    const m = (bytes[0] << 24 | bytes[1] << 16 | bytes[2] << 8 | bytes[3]) >>> 0;
    if (m === 0x80371240) return 'starts like an N64 ROM (.z64 byte order)';
    if (m === 0x37804012) return 'starts like an N64 ROM (.v64 byte order)';
    if (m === 0x40123780) return 'starts like an N64 ROM (.n64 byte order)';
  }
  if (bytes.length >= 0xc0 && bytes[0xb2] === 0x96) {
    // GBA header complement check over 0xA0..0xBC
    let c = 0;
    for (let i = 0xa0; i <= 0xbc; i++) c = (c - bytes[i]) & 0xff;
    c = (c - 0x19) & 0xff;
    if (c === bytes[0xbd]) return 'has a valid GBA cartridge header';
  }
  return null;
}
